import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useOutcomes } from '@/entities/call'
import {
  CONTACT_LABELS,
  CONTACT_STATUS,
  useContact,
  useUpdateContactProfile,
} from '@/entities/contact'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { Button, Input, Select, SkeletonStack, Textarea } from '@/shared/ui'
import { ApiError } from '@/shared/api/client'
import { formatDateTime } from '@/shared/lib/time'
import type {
  ChangeEvent,
  ClientProfileFieldKey,
  ContactCall,
  ContactDetails,
  ContactStatus,
  ProfileField,
} from '@/shared/api/types'
import styles from './ClientPage.module.css'

type Editor =
  | { kind: 'summary' }
  | { kind: 'field'; key: ClientProfileFieldKey }

export function ClientPage() {
  const { id } = useParams<{ id: string }>()
  const parsedId = Number(id)
  const clientId = Number.isSafeInteger(parsedId) && parsedId > 0 ? parsedId : null
  const contact = useContact(clientId)

  if (clientId === null) {
    return <ClientState title="Клиент не найден" text="Проверьте адрес карточки." />
  }

  if (contact.isPending) {
    return <ClientLoading />
  }

  if (contact.isError) {
    const missing = contact.error instanceof ApiError && contact.error.status === 404
    return (
      <ClientState
        title={missing ? 'Клиент не найден' : 'Не удалось открыть карточку'}
        text={missing ? 'Возможно, контакт удалён.' : errorMessage(contact.error)}
        onRetry={missing ? undefined : () => void contact.refetch()}
        retrying={contact.isFetching}
      />
    )
  }

  if (!contact.data) {
    return <ClientState title="Клиент не найден" text="В карточке нет данных." />
  }

  return <ClientCard clientId={clientId} details={contact.data} />
}

function ClientCard({ clientId, details }: { clientId: number; details: ContactDetails }) {
  const update = useUpdateContactProfile(clientId)
  const outcomes = useOutcomes()
  const [editor, setEditor] = useState<Editor | null>(null)
  const [draft, setDraft] = useState('')
  // Ошибку показываем там, где её сделали: у метки или у открытого поля.
  const [labelError, setLabelError] = useState<string | null>(null)
  const [editError, setEditError] = useState<string | null>(null)

  const { contact, profile, calls, history } = details
  const nameField = profile.fields.find((field) => field.key === 'name')
  const facts = profile.fields.filter((field) => field.key !== 'name')
  const savedName = nameField?.value.trim() ?? ''
  const displayName = savedName || contact.displayName.trim() || contact.phoneFormatted || 'Без имени'
  const labelId = `client-label-${clientId}`

  const outcomeLabels = useMemo(
    () => new Map((outcomes.data ?? []).map((outcome) => [outcome.value, outcome.label])),
    [outcomes.data],
  )

  const currentValue = editor
    ? editor.kind === 'summary'
      ? profile.summary
      : (profile.fields.find((field) => field.key === editor.key)?.value ?? '')
    : ''
  const editorDirty = editor !== null && draft.trim() !== currentValue.trim()

  function editSummary() {
    setEditor({ kind: 'summary' })
    setDraft(profile.summary)
    setEditError(null)
  }

  function editField(key: ClientProfileFieldKey, value: string) {
    setEditor({ kind: 'field', key })
    setDraft(value)
    setEditError(null)
  }

  function cancelEdit() {
    if (update.isPending) return
    setEditor(null)
    setDraft('')
    setEditError(null)
  }

  function saveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!editor || !editorDirty) return

    const value = draft.trim()
    const body =
      editor.kind === 'summary'
        ? { summary: value }
        : { fields: { [editor.key]: value } }

    setEditError(null)
    update.mutate(body, {
      onSuccess: () => {
        setEditor(null)
        setDraft('')
      },
      onError: (error) => setEditError(saveErrorMessage(error)),
    })
  }

  function saveLabel(value: string) {
    const label = CONTACT_LABELS.find((option) => option.value === value)?.value
    if (label === undefined || label === profile.label) return
    setLabelError(null)
    update.mutate({ label }, { onError: (error) => setLabelError(saveErrorMessage(error)) })
  }

  const nameEditing = editor?.kind === 'field' && editor.key === 'name'
  const summaryEditing = editor?.kind === 'summary'

  return (
    <>
      <header className={styles.clientHeader}>
        <div className={styles.identity}>
          <span className={styles.eyebrow}>Карточка клиента</span>

          <div className={styles.nameRow}>
            <h1 className={styles.clientName}>{displayName}</h1>
            {!nameEditing && (
              <button
                type="button"
                className={styles.editButton}
                onClick={() => editField('name', nameField?.value ?? '')}
                disabled={update.isPending}
              >
                {savedName ? 'Изменить' : 'Добавить имя'}
              </button>
            )}
          </div>

          {nameEditing && (
            <InlineEditor
              label="Имя клиента"
              value={draft}
              onChange={setDraft}
              onSubmit={saveEdit}
              onCancel={cancelEdit}
              saving={update.isPending}
              canSave={editorDirty}
              error={editError}
            />
          )}

          {!nameEditing && nameField && <FieldSource field={nameField} />}

          <div className={styles.phoneRow}>
            <a className={styles.phone} href={phoneHref(contact.phone)}>
              {contact.phoneFormatted || contact.phone}
            </a>
            <span className={`${styles.status} ${statusClass(contact.status)}`}>
              {CONTACT_STATUS[contact.status].label}
            </span>
          </div>
        </div>

        <div className={styles.labelControl}>
          <label className={styles.labelTitle} htmlFor={labelId}>
            Метка
          </label>
          <Select
            id={labelId}
            options={CONTACT_LABELS.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            value={profile.label}
            onChange={(event) => saveLabel(event.currentTarget.value)}
            disabled={update.isPending}
          />
          <span className={styles.labelHint}>Сохраняется сразу</span>
          {labelError && (
            <span className={styles.saveError} role="alert">
              {labelError}
            </span>
          )}
        </div>
      </header>

      <div className={styles.columns}>
        <div className={styles.mainColumn}>
          <section className={styles.panel} aria-labelledby="client-summary-title">
            <PanelHead
              id="client-summary-title"
              title="Кратко"
              hint="Что важно помнить перед следующим звонком"
              action={
                !summaryEditing ? (
                  <button
                    type="button"
                    className={styles.editButton}
                    onClick={editSummary}
                    disabled={update.isPending}
                  >
                    {profile.summary ? 'Изменить' : 'Добавить'}
                  </button>
                ) : undefined
              }
            />
            <div className={styles.summaryBody}>
              {summaryEditing ? (
                <InlineEditor
                  label="Краткая сводка"
                  value={draft}
                  onChange={setDraft}
                  onSubmit={saveEdit}
                  onCancel={cancelEdit}
                  saving={update.isPending}
                  canSave={editorDirty}
                  multiline
                  error={editError}
                />
              ) : profile.summary ? (
                <p className={styles.summaryText}>{profile.summary}</p>
              ) : (
                <p className={styles.emptyText}>Сводки пока нет</p>
              )}

              {!summaryEditing && profile.updatedAt && (
                <time className={styles.updatedAt} dateTime={profile.updatedAt}>
                  Карточка обновлена {formatDateTime(profile.updatedAt)}
                </time>
              )}
            </div>
          </section>

          <section className={styles.panel} aria-labelledby="client-profile-title">
            <PanelHead
              id="client-profile-title"
              title="Профиль"
              hint="Факты, которые агент учитывает в разговоре"
            />

            {facts.length > 0 ? (
              <div className={styles.factList}>
                {facts.map((field) => {
                  const editing = editor?.kind === 'field' && editor.key === field.key
                  return (
                    <FactRow
                      key={field.key}
                      field={field}
                      editing={editing}
                      draft={draft}
                      onDraft={setDraft}
                      onEdit={() => editField(field.key, field.value)}
                      onSubmit={saveEdit}
                      onCancel={cancelEdit}
                      saving={update.isPending}
                      canSave={editing && editorDirty}
                      error={editing ? editError : null}
                    />
                  )
                })}
              </div>
            ) : (
              <p className={styles.panelEmpty}>Профиль пока пуст</p>
            )}
          </section>
        </div>

        <CallsPanel calls={calls} outcomeLabels={outcomeLabels} />
      </div>

      <HistoryPanel history={history} />
    </>
  )
}

interface PanelHeadProps {
  id: string
  title: string
  hint?: string
  action?: ReactNode
}

function PanelHead({ id, title, hint, action }: PanelHeadProps) {
  return (
    <div className={styles.panelHead}>
      <div className={styles.panelTitles}>
        <h2 id={id} className={styles.panelTitle}>
          {title}
        </h2>
        {hint && <p className={styles.panelHint}>{hint}</p>}
      </div>
      {action && <div className={styles.panelAction}>{action}</div>}
    </div>
  )
}

interface InlineEditorProps {
  label: string
  value: string
  onChange: (value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  saving: boolean
  canSave: boolean
  multiline?: boolean
  error?: string | null
}

function InlineEditor({
  label,
  value,
  onChange,
  onSubmit,
  onCancel,
  saving,
  canSave,
  multiline = false,
  error = null,
}: InlineEditorProps) {
  return (
    <form className={styles.inlineEditor} onSubmit={onSubmit}>
      {multiline ? (
        <Textarea
          className={styles.inlineTextarea}
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          aria-label={label}
          autoFocus
        />
      ) : (
        <Input
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          aria-label={label}
          autoFocus
        />
      )}
      <div className={styles.inlineActions}>
        <Button type="submit" size="sm" loading={saving} disabled={!canSave}>
          Сохранить
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
      {error && (
        <p className={styles.saveError} role="alert">
          {error}
        </p>
      )}
    </form>
  )
}

interface FactRowProps {
  field: ProfileField
  editing: boolean
  draft: string
  onDraft: (value: string) => void
  onEdit: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  saving: boolean
  canSave: boolean
  error: string | null
}

function FactRow({
  field,
  editing,
  draft,
  onDraft,
  onEdit,
  onSubmit,
  onCancel,
  saving,
  canSave,
  error,
}: FactRowProps) {
  return (
    <div className={styles.factRow}>
      <div className={styles.factLabel}>{field.label}</div>
      <div className={styles.factContent}>
        {editing ? (
          <InlineEditor
            label={field.label}
            value={draft}
            onChange={onDraft}
            onSubmit={onSubmit}
            onCancel={onCancel}
            saving={saving}
            canSave={canSave}
            multiline={isMultilineField(field.key)}
            error={error}
          />
        ) : (
          <>
            <div className={field.value ? styles.factValue : styles.factEmpty}>
              {field.value || 'Не заполнено'}
            </div>
            <FieldSource field={field} />
          </>
        )}
      </div>
      {!editing && (
        <button
          type="button"
          className={styles.editButton}
          onClick={onEdit}
          disabled={saving}
          aria-label={`Изменить поле «${field.label}»`}
        >
          Изменить
        </button>
      )}
    </div>
  )
}

function FieldSource({ field }: { field: ProfileField }) {
  if (!field.callId && !field.manual && !field.updatedAt) return null

  return (
    <div className={styles.fieldMeta}>
      {field.callId && (
        <Link className={styles.sourceLink} to={`/calls/${field.callId}`}>
          Звонок #{field.callId}
        </Link>
      )}
      {field.manual && (
        <span className={styles.manualMark} title="Агент не перезапишет ручную правку">
          Вручную
        </span>
      )}
      {field.updatedAt && (
        <time className={styles.metaTime} dateTime={field.updatedAt}>
          {formatDateTime(field.updatedAt)}
        </time>
      )}
    </div>
  )
}

function CallsPanel({
  calls,
  outcomeLabels,
}: {
  calls: ContactCall[]
  outcomeLabels: ReadonlyMap<string, string>
}) {
  return (
    <section className={`${styles.panel} ${styles.callsPanel}`} aria-labelledby="client-calls-title">
      <PanelHead
        id="client-calls-title"
        title="Звонки"
        action={<span className={styles.count}>{calls.length}</span>}
      />

      {calls.length > 0 ? (
        <div className={styles.callList}>
          {calls.map((call) => (
            <Link key={call.id} className={styles.callRow} to={`/calls/${call.id}`}>
              <div className={styles.callHead}>
                <span className={styles.callOutcome}>{callOutcome(call, outcomeLabels)}</span>
                <time className={styles.callDate} dateTime={call.createdAt}>
                  {formatDateTime(call.createdAt)}
                </time>
              </div>
              <p className={styles.callSummary}>{call.summary || call.error || 'Без сводки'}</p>
              <div className={styles.callMeta}>
                <span>{formatDuration(call.durationSec)}</span>
                {call.hasRecording && <span className={styles.recordingMark}>Есть запись</span>}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className={styles.panelEmpty}>Звонков пока нет</p>
      )}
    </section>
  )
}

function HistoryPanel({ history }: { history: ChangeEvent[] }) {
  return (
    <section className={styles.panel} aria-labelledby="client-history-title">
      <PanelHead
        id="client-history-title"
        title="История изменений"
        hint="Что менялось в карточке и откуда это известно"
        action={<span className={styles.count}>{history.length}</span>}
      />

      {history.length > 0 ? (
        <ol className={styles.timeline}>
          {history.map((event) => {
            const before = eventValue(event, event.before, 'не было')
            const after = eventValue(event, event.after, 'очищено')
            return (
              <li key={event.id} className={styles.timelineItem}>
                <div className={styles.timelineRail} aria-hidden="true">
                  <span
                    className={`${styles.timelineDot} ${event.important ? styles.timelineDotImportant : ''}`}
                  />
                </div>
                <article
                  className={`${styles.event} ${event.important ? styles.eventImportant : ''}`}
                >
                  <div className={styles.eventHead}>
                    <span className={styles.eventField}>{event.fieldLabel || 'Изменение'}</span>
                    {event.important && <span className={styles.importantMark}>Важно</span>}
                    <time className={styles.eventDate} dateTime={event.at}>
                      {formatDateTime(event.at)}
                    </time>
                  </div>

                  <div className={styles.change}>
                    <span className={styles.changeSide}>
                      <span className={styles.changeLabel}>Было</span>
                      <del className={styles.changeBefore}>{before}</del>
                    </span>
                    <span className={styles.changeArrow} aria-hidden="true">
                      →
                    </span>
                    <span className={styles.changeSide}>
                      <span className={styles.changeLabel}>Стало</span>
                      <strong className={styles.changeAfter}>{after}</strong>
                    </span>
                  </div>

                  <div className={styles.eventMeta}>
                    <span>Изменил: {eventAuthor(event)}</span>
                    {event.callId && (
                      <Link
                        className={styles.sourceLink}
                        to={callHref(event.callId, event.offsetMs)}
                      >
                        Звонок #{event.callId}
                        {event.offsetMs !== null ? ` · ${formatOffset(event.offsetMs)}` : ''}
                      </Link>
                    )}
                  </div>
                </article>
              </li>
            )
          })}
        </ol>
      ) : (
        <p className={styles.panelEmpty}>Изменений пока нет</p>
      )}
    </section>
  )
}

function ClientLoading() {
  return (
    <>
      <PageHeader title="Карточка клиента" />
      <div className={styles.loadingGrid} aria-label="Загружаем карточку">
        <section className={styles.panel}>
          <div className={styles.loadingBody}>
            <SkeletonStack rows={3} height={36} />
          </div>
        </section>
        <section className={styles.panel}>
          <div className={styles.loadingBody}>
            <SkeletonStack rows={6} height={44} />
          </div>
        </section>
      </div>
    </>
  )
}

function ClientState({
  title,
  text,
  onRetry,
  retrying = false,
}: {
  title: string
  text: string
  onRetry?: () => void
  retrying?: boolean
}) {
  return (
    <>
      <PageHeader title={title} />
      <section className={styles.statePanel} role="alert">
        <p className={styles.stateText}>{text}</p>
        <div className={styles.stateActions}>
          {onRetry && (
            <Button size="sm" onClick={onRetry} loading={retrying}>
              Повторить
            </Button>
          )}
          <Link className={styles.stateLink} to="/campaigns">
            К базе номеров
          </Link>
        </div>
      </section>
    </>
  )
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'Попробуйте ещё раз.'
}

function saveErrorMessage(error: unknown): string {
  return `Не сохранилось: ${errorMessage(error)}`
}

function phoneHref(phone: string): string {
  if (!phone) return 'tel:'
  return `tel:${phone.startsWith('+') ? phone : `+${phone}`}`
}

function statusClass(status: ContactStatus): string {
  if (status === 'talked') return styles.statusSuccess ?? ''
  if (status === 'no_answer') return styles.statusWarning ?? ''
  if (status === 'do_not_call') return styles.statusDanger ?? ''
  return ''
}

function isMultilineField(key: ClientProfileFieldKey): boolean {
  return !['name', 'position', 'company', 'city', 'tone'].includes(key)
}

function callOutcome(call: ContactCall, labels: ReadonlyMap<string, string>): string {
  if (call.outcome) return labels.get(call.outcome) ?? call.outcome.replaceAll('_', ' ')
  if (call.error || call.status === 'failed') return 'Не состоялся'
  if (call.status === 'completed') return 'Завершён'
  return 'Без итога'
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return 'Без разговора'
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${minutes}:${String(rest).padStart(2, '0')}`
}

function callHref(callId: number, offsetMs: number | null): string {
  return `/calls/${callId}${offsetMs === null ? '' : `?t=${offsetMs}`}`
}

function formatOffset(offsetMs: number): string {
  const seconds = Math.max(0, Math.floor(offsetMs / 1000))
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

function eventAuthor(event: ChangeEvent): string {
  const name = event.authorName.trim()
  if (name) return name
  return event.author === 'agent' ? 'Агент' : 'Пользователь'
}

function eventValue(event: ChangeEvent, value: string, empty: string): string {
  const clean = value.trim()

  if (event.kind === 'label' || event.field === 'label') {
    return CONTACT_LABELS.find((label) => label.value === clean)?.label ?? clean
  }

  if (!clean) return empty

  if (event.kind === 'status' || event.field === 'status') {
    const status = Object.entries(CONTACT_STATUS).find(([key]) => key === clean)
    return status?.[1].label ?? clean
  }

  return clean
}
